import React, { useRef, useState, useEffect, Fragment } from "react";
import { gsap } from "gsap";
import { useSwipeable } from "react-swipeable";
import "./PhotoCarousel.scss";

function PhotoCarousel() {
  const captions = [
    {
      body: "Only agents who belong to the National Association of REALTORS® (NAR) and adhere to its code of ethics can call themselves REALTORS®. Here are three ways that REALTORS® can help buyers find their ideal home:",
      source: true,
    },
    {
      number: 1,
      title: "Patience",
      body: "In a competitive market where many homes receive multiple offers, it is easy to get caught up in emotional bidding wars. “The single most important thing for buyers to have is patience during the home buying process,” says Dr. Lautz. “An agent who is a REALTOR® can save you from paying too much by providing prices of comparable homes [comps] that sold recently in the neighborhood. The comps can help you figure out if a home that interests you is priced correctly.”",
    },
    {
      number: 2,
      title: "Compromise",
      body: "Most home buyers have to compromise on something. They might need to go above their ideal offer price, consider fixer-uppers that need some repairs, or expand the geographic boundaries of their search. But if there’s something that’s so important to you that you refuse to budge—whether it’s the quality of the school district or the proximity to a nice dog park—it’s important to set your limits and then communicate those with your agent.",
    },
    {
      number: 3,
      title: "Expertise",
      body: "Agents who are REALTORS® know what homes are on the market and have the expertise to help buyers decide where to focus or expand their search areas. They can also help buyers with negotiations. Even if a buyer can’t put together an all-cash offer, the REALTOR® may offer suggestions on other ways to improve the bid, such as with an earlier closing date. On the flip side, REALTORS® also have the know-how to help sellers get top dollar for their properties. In 2021, NAR research showed that almost two-thirds of sellers received 100% or more of their asking price.",
      source: true,
    },
  ];
  const length = captions.length - 1;
  const [current, setCurrent] = useState(0);

  const container = useRef();
  const photoRef = useRef();

  const handlers = useSwipeable({
    onSwipedLeft: () => handleClick("right"),
    onSwipedRight: () => handleClick("left"),
  });
  const handleClick = (direction) => {
    let currentI;
    container.current.style.animation = "fade-out 0.7s ease-in forwards";
    setTimeout(() => {
      container.current.style.animation = "fade-in 0.7s ease-in forwards";
      setCurrent(currentI);
    }, 700);
    if (direction === "right") {
      if (current === length) {
        photoRef.current.style.animation = "moveTo1R 1s ease-in forwards";
      } else {
        photoRef.current.style.animation = `moveTo${
          current + 2
        }R 1s ease-in forwards`;
      }
      currentI = current === length ? 0 : current + 1;
    } else {
      if (current === 0) {
        photoRef.current.style.animation = "moveTo4 1s ease-in forwards";
      } else {
        photoRef.current.style.animation = `moveTo${current} 1s ease-in forwards`;
      }

      currentI = current === 0 ? length : current - 1;
    }
  };

  useEffect(() => {
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: container.current,
        start: "top 75%",
        end: "top 75%",
        once: true,
      },
    });
    tl.fromTo("#helpIcon", { y: 20 }, { y: 0, duration: 1 }).fromTo(
      "#realtor",
      {
        opacity: 0,
      },
      {
        opacity: 1,
        duration: 1,
      }
    );
  }, []);

  return (
    <div className="photoCarousel">
      <div className="indicator-container indicator-container-mobile ">
        <div className="indicator">
          <button
            className="nav-button nav-button-back"
            onClick={() => {
              handleClick("left");
            }}
          >
            BACK
          </button>
          <p className="nav-divider">|</p>
          <button
            className="nav-button nav-button-next"
            onClick={() => {
              handleClick("right");
            }}
          >
            NEXT
          </button>
        </div>
      </div>
      <div className="photoCarousel__photoContainer" {...handlers}>
        <div className="photoCarousel__photoContainer-container" ref={photoRef}>
          {captions.map((caption, i) => {
            return (
              <Fragment key={i}>
                <div
                  className={`photoCarousel-photo photoCarousel-photo-${i}`}
                />
                {caption.source ? (
                  <p
                    className={`photoCarousel-source photoCarousel-source-${i}`}
                  >
                    Source: National Association of REALTORS®
                  </p>
                ) : (
                  <></>
                )}
              </Fragment>
            );
          })}
        </div>
      </div>
      <div className="photoCarousel-copy">
        <div className="photoCarousel__copyContainer" ref={container}>
          {current === 0 ? (
            <>
              <svg
                id="helpIcon"
                data-name="Layer 1"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 55.78 50"
                className="photoCarousel__icon"
              >
                <path
                  className="cls-1P"
                  d="M51.46,7h-17V4.29A4.21,4.21,0,0,0,30.15,0H4.4A4.21,4.21,0,0,0,.11,4.29v35.9A2.87,2.87,0,0,0,3,43.05a3,3,0,0,0,2.29-1.14l5.86-7.58h6.15V37a4.34,4.34,0,0,0,4.44,4.29H44.59l6.15,7.58a3,3,0,0,0,4.15.43,2.57,2.57,0,0,0,1-2.14V11.3A4.52,4.52,0,0,0,51.46,7Zm-41,24.46A1.77,1.77,0,0,0,9.26,32L3,40.19V4.29A1.35,1.35,0,0,1,4.4,2.86H30.15a1.35,1.35,0,0,1,1.43,1.43V7H21.71a4.34,4.34,0,0,0-4.44,4.29V31.47Z"
                  transform="translate(-0.11)"
                />
                <path
                  className="cls-1P"
                  d="M43,19.9H40.8v9.15a.92.92,0,0,1-.85.86h-4a.38.38,0,0,1-.43-.43V23.62a.38.38,0,0,0-.43-.43H30.65a.38.38,0,0,0-.43.43v5.86a.38.38,0,0,1-.43.43h-4a.92.92,0,0,1-.86-.86V19.9H22.78c-.71,0-.86-.43-.28-.86L32.37,9.6a.9.9,0,0,1,1.28,0L43.38,19C43.81,19.61,43.66,19.9,43,19.9Z"
                  transform="translate(-0.11)"
                />
                <path
                  id="realtor"
                  className="cls-2P"
                  d="M46.45,24.17H44.3v9.15a.92.92,0,0,1-.85.86h-4a.38.38,0,0,1-.43-.43V27.89a.38.38,0,0,0-.43-.43H34.15a.38.38,0,0,0-.43.43v5.86a.38.38,0,0,1-.43.43h-4a.92.92,0,0,1-.86-.86V24.17H26.28c-.71,0-.85-.43-.28-.86l9.87-9.44a.9.9,0,0,1,1.28,0l9.73,9.44C47.31,23.88,47.17,24.17,46.45,24.17Z"
                  transform="translate(-0.11)"
                />
              </svg>
              <p className="photoCarousel__title">
                How REALTORS® can help with a home search
              </p>
              <p className="photoCarousel__copy">{captions[0].body}</p>
            </>
          ) : (
            <p className="photoCarousel__copy">
              {captions[current].number}.{" "}
              <span className="photoCarousel__copy--bold ">
                {captions[current].title}
              </span>
              : {captions[current].body}
            </p>
          )}
        </div>
        <div className="indicator-container indicator-container-desktop ">
          <div className="indicator">
            <button
              className="nav-button nav-button-back"
              onClick={() => {
                handleClick("left");
              }}
            >
              BACK
            </button>
            <p className="nav-divider">|</p>
            <button
              className="nav-button nav-button-next"
              onClick={() => {
                handleClick("right");
              }}
            >
              NEXT
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PhotoCarousel;
